import { useCallback, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { sessionsService } from '../../../services/sessions/sessions.service';
import { printersService } from '../../../services/printers/printers.service';
import type { PrinterView } from '../../../services/printers/printers.service';
import { receiptService } from '../../../services/receipt/receipt.service';
import { captureReceiptSnapshot } from '../components/receipt/shared/receipt.utils';
import { resolveReceiptPrintTarget } from './fixed-printer.util';
import type { CashDrawerPickPrompt } from '../components/cashdrawerdialog/CashDrawerPickModal';
import type { CashMoveDirection } from '../components/cashmove/CashMoveModal';

type UseCashMoveFlowParams = {
  tenantId: string;
  employeeSession: string | null;
  setError: (message: string | null) => void;
};

// Summary: Owns the cash in/out flow — drawer resolution (0/1/many), the POST and the slip print.
export function useCashMoveFlow({ tenantId, employeeSession, setError }: UseCashMoveFlowParams) {
  const [isOpen, setIsOpen] = useState(false);
  const [drawerPrompt, setDrawerPrompt] = useState<CashDrawerPickPrompt | null>(null);
  const [selectedDrawer, setSelectedDrawer] = useState<{ id: number; name: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [printers, setPrinters] = useState<PrinterView[]>([]);
  const [header, setHeader] = useState('');
  const [printerPick, setPrinterPick] = useState<PrinterView[] | null>(null);
  const slipRef = useRef<HTMLDivElement>(null);
  const requestInFlightRef = useRef(false);
  const saveInFlightRef = useRef(false);

  // Summary: 0 drawers ⇒ record without one, 1 ⇒ auto-select, ≥2 ⇒ pick the drawer first.
  const request = useCallback(async () => {
    if (!employeeSession || requestInFlightRef.current) return;
    requestInFlightRef.current = true;
    try {
      const [activePrinters, tenantHeader] = await Promise.all([
        printersService.listActive(tenantId, employeeSession),
        receiptService.getTenantVatRate(tenantId, employeeSession).then((data) => data.header).catch(() => ''),
      ]);
      setPrinters(activePrinters);
      setHeader(tenantHeader);
      const drawers = activePrinters
        .filter((printer) => printer.cashDrawer)
        .map((printer) => ({ id: printer.id, name: printer.name }));

      if (drawers.length > 1) {
        setDrawerPrompt({ drawers });
        return;
      }

      setSelectedDrawer(drawers[0] ?? null);
      setIsOpen(true);
    } catch (requestError: unknown) {
      setError(requestError instanceof Error ? requestError.message : 'Failed loading cash drawers');
    } finally {
      requestInFlightRef.current = false;
    }
  }, [employeeSession, setError, tenantId]);

  const pickDrawer = useCallback((drawerId: number) => {
    const picked = drawerPrompt?.drawers.find((drawer) => drawer.id === drawerId) ?? null;
    setDrawerPrompt(null);
    setSelectedDrawer(picked);
    setIsOpen(true);
  }, [drawerPrompt]);

  const dismissDrawerPrompt = useCallback(() => setDrawerPrompt(null), []);

  const close = useCallback(() => {
    if (saveInFlightRef.current) return;
    setIsOpen(false);
    setSelectedDrawer(null);
  }, []);

  // Summary: Closes the flow once the slip print settled; a skipped print gets a neutral toast.
  const finish = useCallback((printed = true) => {
    saveInFlightRef.current = false;
    setIsSaving(false);
    setPrinterPick(null);
    setIsOpen(false);
    setSelectedDrawer(null);
    notifications.show(
      printed
        ? { color: 'green', message: 'נרשמה תנועת מזומן', autoClose: 3000 }
        : { color: 'blue', message: 'התנועה נרשמה, ללא הדפסה', autoClose: 3000 },
    );
  }, []);

  // Summary: Captures the offscreen slip and queues it on the report print route; false on failure.
  const printSlip = useCallback(async (printerId: number): Promise<boolean> => {
    try {
      const image = await captureReceiptSnapshot(slipRef.current);
      if (!image) throw new Error('snapshot failed');
      await printersService.printReport(tenantId, printerId, image, employeeSession);
      return true;
    } catch {
      notifications.show({ color: 'red', message: 'הדפסת שובר תנועת המזומן נכשלה', autoClose: 3000 });
      return false;
    }
  }, [employeeSession, tenantId]);

  const confirm = useCallback(async (direction: CashMoveDirection, amount: number, reason: string) => {
    if (!employeeSession || saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    setIsSaving(true);
    setError(null);
    try {
      await sessionsService.recordCashMove(tenantId, employeeSession, {
        direction,
        amount,
        reason,
        ...(selectedDrawer ? { printerId: selectedDrawer.id } : {}),
      });
    } catch {
      setError('רישום תנועת המזומן נכשל');
      saveInFlightRef.current = false;
      setIsSaving(false);
      return;
    }
    // The chosen drawer IS a printer; drawer-less tenants fall back to the report print ladder.
    const target = selectedDrawer ? { kind: 'print' as const, printerId: selectedDrawer.id } : resolveReceiptPrintTarget(printers);
    if (target.kind === 'pick') {
      // The move is recorded; the picker must stay usable, the save guard stays armed until finish.
      setIsSaving(false);
      setPrinterPick(target.candidates ?? printers);
      return;
    }
    let printed = false;
    if (target.kind === 'print') {
      printed = await printSlip(target.printerId);
    } else {
      notifications.show({ color: 'red', message: 'אין מדפסת זמינה להדפסת השובר', autoClose: 3000 });
    }
    finish(printed);
  }, [employeeSession, finish, printSlip, printers, selectedDrawer, setError, tenantId]);

  const pickPrinter = useCallback(async (printerId: number) => {
    setPrinterPick(null);
    setIsSaving(true);
    finish(await printSlip(printerId));
  }, [finish, printSlip]);

  const dismissPrinterPick = useCallback(() => finish(false), [finish]);

  return {
    isOpen,
    drawerPrompt,
    drawerName: selectedDrawer?.name ?? null,
    header,
    slipRef,
    printerPick,
    isSaving,
    request,
    pickDrawer,
    dismissDrawerPrompt,
    pickPrinter,
    dismissPrinterPick,
    close,
    confirm,
  };
}
